import vipBasicIcon from '@src/assets/images/vip-basic.png';
import vipStandardIcon from '@src/assets/images/vip-standard.png';
import vipPremiumIcon from '@src/assets/images/vip-premium.png';
import vipBasicBadge from '@src/assets/images/vip-badge-basic.png';
import vipStandardBadge from '@src/assets/images/vip-badge-standard.png';
import vipPremiumBadge from '@src/assets/images/vip-badge-premium.png';
import { useStoreMapSettings } from '@src/store/modules/mapSettings';

// ---------------------------- 城市兜底 ----------------------------

/** 无本地/接口城市数据时的兜底城市（合肥市） */
export const DEFAULT_CITY = {
    id: 1,
    name: '合肥市',
    mapId: 1,
    areaId: 3401,
    latlon: [117.227267, 31.820567],
};

// ---------------------------- 地图缩放 ----------------------------

/** 地图缩放级别配置 */
export const MAP_ZOOM_LEVEL = {
    maxZoom: 20,
    minZoom: 8,
    defaultZoom: 12,
};

// ---------------------------- VIP 会员 ----------------------------

/** 地图 VIP 版本：0 基础版 / 1 标准版 / 2 全能版 */
export const MAP_VIP_VERSION = {
    BASIC: 0,
    STANDARD: 1,
    PREMIUM: 2,
};

/** 地图 VIP 状态：0 在期 / 1 过期 */
export const MAP_VIP_STATUS = {
    ACTIVE: '0',
    EXPIRED: '1',
};

/** VIP 版本与图标映射 */
export const MAP_VIP_VERSION_TO_ICON = {
    [MAP_VIP_VERSION.BASIC]: vipBasicIcon,
    [MAP_VIP_VERSION.STANDARD]: vipStandardIcon,
    [MAP_VIP_VERSION.PREMIUM]: vipPremiumIcon,
};

/** VIP 版本与徽章映射（账户菜单头像角标） */
export const MAP_VIP_VERSION_TO_BADGE = {
    [MAP_VIP_VERSION.BASIC]: vipBasicBadge,
    [MAP_VIP_VERSION.STANDARD]: vipStandardBadge,
    [MAP_VIP_VERSION.PREMIUM]: vipPremiumBadge,
};

/** 基础版会员 VR 显示水印（URL 参数 showMark=1），其余版本不显示 */
export function resolveVrShowMark(vipVersion) {
    return String(vipVersion) === '0' ? '1' : '0';
}

/** 向 URL 追加或覆盖 query 参数 */
export function appendUrlSearchParams(url, paramsObj) {
    const urlObj = new URL(url);
    Object.entries(paramsObj).forEach(([key, value]) => {
        if (value != null && value !== '') {
            urlObj.searchParams.set(key, String(value));
        }
    });
    return urlObj.toString();
}

/**
 * 构建 VR 全屏预览 URL（追加 mapId、showMark）。
 * @param {string} vrHttpUrl
 * @param {{ mapId?: string | number, vipVersion?: number }} [options]
 */
export function buildVrPreviewUrl(vrHttpUrl, { mapId, vipVersion } = {}) {
    if (!vrHttpUrl) return '';
    const params = { showMark: resolveVrShowMark(vipVersion) };
    if (mapId != null && mapId !== '') {
        params.mapId = mapId;
    }
    return appendUrlSearchParams(vrHttpUrl, params);
}

// ---------------------------- 底图样式（内部） ----------------------------

/** 矢量底图可选样式 ID 列表 */
export const VECTOR_MAP_STYLE_TYPES = ['style5', 'style4', 'style3', 'style1', 'style2', 'style6'];

/** 默认矢量底图样式 */
export const DEFAULT_MAP_STYLE_ID = 'style2';

/** WGS 坐标系卫星图（ArcGIS 影像）；其余底图为火星坐标（GCJ-02） */
export const WGS_SATELLITE_MAP_TYPES = Object.freeze(['arcgis']);

/** 当前底图是否为 WGS 卫星（卫星 C） */
export const isWgsSatelliteMapType = type => WGS_SATELLITE_MAP_TYPES.includes(type);

/**
 * 解析初始底图样式 ID。
 * 优先使用 store 中已激活的底图类型，无效时回退到默认样式。
 */
const resolveInitialMapStyleId = activeBaseMapType => {
    if (activeBaseMapType && VECTOR_MAP_STYLE_TYPES.includes(activeBaseMapType)) {
        return activeBaseMapType;
    }
    return DEFAULT_MAP_STYLE_ID;
};

const cloneMapLayer = layer => ({
    ...layer,
    ...(Array.isArray(layer.features) ? { features: [...layer.features] } : {}),
});

// ---------------------------- 腾讯地图 SDK ----------------------------

/**
 * 动态加载腾讯地图 GL JS SDK。
 * @param {string} key 腾讯地图 API Key
 * @returns {Promise<typeof TMap>}
 */
export function TMapLoader(key) {
    return new Promise((resolve, reject) => {
        window.init = function () {
            resolve(window.TMap);
        };
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://map.qq.com/api/gljs?v=1.exp&callback=init&key=${key}&libraries=view,geometry,model,tools,visualization,service`;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

// ---------------------------- 地图初始化 ----------------------------

/**
 * 创建腾讯地图实例并挂载到 DOM。
 * 底图图层（矢量 / 路况 / 卫星）从 mapSettings store 读取，缺失时写入默认值。
 *
 * @param {typeof TMap} TMap 腾讯地图 SDK 命名空间
 * @param {object} currentCity 当前城市，含 latlon: [lng, lat]
 * @param {string} [domId='mapContainer'] 地图容器元素 ID
 * @param {object} [option={}] 透传给 TMap.Map 的额外配置
 * @returns {Promise<{ map: TMap.Map, measureTool: TMap.tools.MeasureTool }>}
 */
export async function initMap(TMap, currentCity, domId = 'mapContainer', option = {}) {
    const mapSettingsStore = useStoreMapSettings();

    return new Promise(resolve => {
        const mapContainer = document.getElementById(`${domId}`);
        // TMap.LatLng 入参为 (lat, lng)，currentCity.latlon 为 [lng, lat]
        const latlng = currentCity?.latlon?.length > 0 ? [currentCity.latlon[1], currentCity.latlon[0]] : [31.820567, 117.227267];
        const baseMapLayers = [];

        const { vectorLayer, trafficLayer, satelliteLayer, activeBaseMapType } = mapSettingsStore.mapSettings;

        // 矢量底图层
        if (vectorLayer) {
            baseMapLayers.push(cloneMapLayer(vectorLayer));
        } else {
            const defaultVectorLayer = {
                type: 'vector',
                features: ['base', 'building2d', 'label', 'building3d', 'point'],
            };
            baseMapLayers.push(defaultVectorLayer);
            mapSettingsStore.patchMapSettings({ vectorLayer: cloneMapLayer(defaultVectorLayer) });
        }

        // 路况图层：叠加后精简矢量层要素，避免与路况冲突
        if (trafficLayer) {
            baseMapLayers.push(cloneMapLayer(trafficLayer));
            baseMapLayers[0].features = ['base', 'label', 'point'];
        }

        // 卫星图层
        if (satelliteLayer) {
            baseMapLayers.push(cloneMapLayer(satelliteLayer));
        }

        mapSettingsStore.patchMapSettings({
            baseMapLayers: baseMapLayers.map(cloneMapLayer),
            vectorLayer: cloneMapLayer(baseMapLayers[0]),
        });

        const map = new TMap.Map(mapContainer, {
            center: new TMap.LatLng(...latlng),
            zoom: MAP_ZOOM_LEVEL.defaultZoom,
            maxZoom: MAP_ZOOM_LEVEL.maxZoom,
            mapStyleId: resolveInitialMapStyleId(activeBaseMapType),
            minZoom: MAP_ZOOM_LEVEL.minZoom,
            baseMap: baseMapLayers.map(cloneMapLayer),
            ...option,
        });

        const mapControl = map.getControl(TMap.constants.DEFAULT_CONTROL_ID.ZOOM);
        mapControl.setPosition(TMap.constants.CONTROL_POSITION.BOTTOM_RIGHT);
        map.getControl(TMap.constants.DEFAULT_CONTROL_ID.ROTATION).setPosition(TMap.constants.CONTROL_POSITION.BOTTOM_RIGHT);

        const measureTool = new TMap.tools.MeasureTool({ map });
        resolve({ map, measureTool });
    });
}

// ---------------------------- IP 定位与城市匹配 ----------------------------

/**
 * adcode 前缀与 city.id 是否从字符串头部互相兼容（模糊前缀匹配）。
 * 二者长度可以不同，例如 adcode 前 3 位 `340` 可匹配 city.id `3401`。
 */
const isAdcodeCityIdPrefixMatch = (adcodePrefix, cityId) => {
    const prefix = String(adcodePrefix);
    const idStr = String(cityId);
    if (!prefix || !idStr) return false;
    return prefix.startsWith(idStr) || idStr.startsWith(prefix);
};

/**
 * 取 adcode 头部若干位，在城市列表中做模糊前缀匹配。
 * 多个命中时：优先完全相等，否则按 city.id 纯数字大小取最小的一项。
 */
const findCityByAdcodeHead = (adcodeStr, cities, headLen) => {
    const adcodePrefix = adcodeStr.slice(0, headLen);
    if (!adcodePrefix) return null;

    const matched = cities.filter(city => isAdcodeCityIdPrefixMatch(adcodePrefix, city.id));
    if (!matched.length) return null;

    const cityInfo = matched.find(city => String(city.id) === adcodePrefix) ?? matched.sort((a, b) => Number(a.id) - Number(b.id))[0];

    return { cityInfo, areaId: cityInfo.id };
};

/**
 * 根据腾讯 IP 定位返回的 adcode，在城市列表中解析匹配项。
 *
 * geo/list 中 city.id 为地级 areaId（如合肥 3401）；接口 adcode 至少 4 位（如 340000、340104）。
 * 匹配时截取 adcode **头部**若干位，与 city.id 做字符串前缀模糊匹配（长度不必相同）。
 *
 * 匹配顺序：
 * 1. adcode 前 4 位模糊匹配，如 340104 → 3401
 * 2. 未命中则 adcode 前 3 位再试，如 340000 → 3401（340 ⊂ 3401）
 * 3. 仍未命中则回退 DEFAULT_CITY.areaId（3401 合肥）
 */
const resolveCityInfoByAdcode = (adcode, cities) => {
    const adcodeStr = String(adcode ?? '').replace(/\D/g, '');
    const fallback = () => ({
        cityInfo: cities.find(city => String(city.id) === String(DEFAULT_CITY.areaId)) ?? null,
        areaId: DEFAULT_CITY.areaId,
    });

    if (!adcodeStr) return fallback();

    return findCityByAdcodeHead(adcodeStr, cities, 4) || findCityByAdcodeHead(adcodeStr, cities, 3) || fallback();
};

/**
 * 通过腾讯 IP 定位获取当前城市，并与系统城市列表匹配后写入 store。
 * 定位失败时使用备用 IP 重试，仍无结果则回退到 DEFAULT_CITY。
 *
 * @param {typeof TMap} TMap 腾讯地图 SDK 命名空间（需已加载 service 库）
 * @returns {Promise<object>} 定位匹配后的城市对象
 */
export const getCurrentCityByGeolocation = async TMap => {
    const { useStoreCity } = await import('@src/store/modules/city');
    let locationRes = null;
    const ipLocation = new TMap.service.IPLocation();

    try {
        locationRes = await ipLocation.locate({ ip: '', servicesk: '' });
    } catch {
        // 空 IP 定位失败时，使用备用 IP 重试
        locationRes = await ipLocation.locate({ ip: '1.2.2.255', servicesk: '' });
    }
    console.log('[ip-location] 腾讯 IP 定位结果', locationRes);

    const { ad_info, location } = locationRes.result || {};

    const storeCity = useStoreCity();
    await storeCity.fetchCityList();

    const { cityInfo, areaId } = resolveCityInfoByAdcode(ad_info?.adcode, storeCity.cityList);

    const currentCityByGeolocation = {
        id: cityInfo?.mapId || DEFAULT_CITY.id,
        name: cityInfo?.name || ad_info?.city || DEFAULT_CITY.name,
        mapId: cityInfo?.mapId || DEFAULT_CITY.mapId,
        areaId,
        latlon: location?.lng != null && location?.lat != null ? [location.lng, location.lat] : DEFAULT_CITY.latlon,
        automaticPositioning: true,
    };

    storeCity.setCityByGeolocation(currentCityByGeolocation);
    return currentCityByGeolocation;
};

// ---------------------------- 坐标转换（GCJ-02 ↔ WGS84） ----------------------------

// eslint-disable-next-line no-loss-of-precision
const GCJ_PI = 3.14159265358979324;
const GCJ_A = 6378245.0;
// eslint-disable-next-line no-loss-of-precision
const GCJ_EE = 0.00669342162296594323;

function transformLat(x, y) {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
    ret += ((20.0 * Math.sin(6.0 * x * GCJ_PI) + 20.0 * Math.sin(2.0 * x * GCJ_PI)) * 2.0) / 3.0;
    ret += ((20.0 * Math.sin(y * GCJ_PI) + 40.0 * Math.sin((y / 3.0) * GCJ_PI)) * 2.0) / 3.0;
    ret += ((160.0 * Math.sin((y / 12.0) * GCJ_PI) + 320 * Math.sin((y * GCJ_PI) / 30.0)) * 2.0) / 3.0;
    return ret;
}

function transformLon(x, y) {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
    ret += ((20.0 * Math.sin(6.0 * x * GCJ_PI) + 20.0 * Math.sin(2.0 * x * GCJ_PI)) * 2.0) / 3.0;
    ret += ((20.0 * Math.sin(x * GCJ_PI) + 40.0 * Math.sin((x / 3.0) * GCJ_PI)) * 2.0) / 3.0;
    ret += ((150.0 * Math.sin((x / 12.0) * GCJ_PI) + 300.0 * Math.sin((x / 30.0) * GCJ_PI)) * 2.0) / 3.0;
    return ret;
}

function delta(lat, lon) {
    let dLat = transformLat(lon - 105.0, lat - 35.0);
    let dLon = transformLon(lon - 105.0, lat - 35.0);
    const radLat = (lat / 180.0) * GCJ_PI;
    let magic = Math.sin(radLat);
    magic = 1 - GCJ_EE * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / (((GCJ_A * (1 - GCJ_EE)) / (magic * sqrtMagic)) * GCJ_PI);
    dLon = (dLon * 180.0) / ((GCJ_A / sqrtMagic) * Math.cos(radLat) * GCJ_PI);
    return { lat: dLat, lon: dLon };
}

/** GCJ-02 → WGS84（卫星 C 业务点对齐 ArcGIS 影像） */
export function transformGCJ2WGS(gcjLat, gcjLon) {
    const d = delta(gcjLat, gcjLon);
    return {
        lat: gcjLat - d.lat,
        lon: gcjLon - d.lon,
    };
}

/** WGS84 → GCJ-02（从卫星 C 切回腾讯底图时还原中心点） */
export function transformWGS2GCJ(wgsLat, wgsLon) {
    const d = delta(wgsLat, wgsLon);
    return {
        lat: wgsLat + d.lat,
        lon: wgsLon + d.lon,
    };
}

// ---------------------------- 坐标工厂 ----------------------------

/**
 * 腾讯地图 LatLng 工厂。
 * 卫星 C（ArcGIS WGS 影像）下将业务点 GCJ→WGS，与瓦片对齐。
 * GeoJSON `[lng, lat]` 传入时写 `createNewTMapLatLng(lat, lng)`。
 *
 * @param {number} latitude
 * @param {number} longitude
 * @param {number} [height]
 */
export function createNewTMapLatLng(latitude, longitude, height) {
    if (!window.TMap) console.warn('未加载腾讯地图');

    let lat = latitude;
    let lng = longitude;
    const activeBaseMapType = useStoreMapSettings().mapSettings.activeBaseMapType;
    if (isWgsSatelliteMapType(activeBaseMapType)) {
        const converted = transformGCJ2WGS(latitude, longitude);
        lat = converted.lat;
        lng = converted.lon;
    }

    return height != null ? new window.TMap.LatLng(lat, lng, height) : new window.TMap.LatLng(lat, lng);
}

// ---------------------------- 商圈贴图旋转（ImageGround） ----------------------------

/** 旋转矩形的最小外接宽高（rotate 为弧度） */
export function getRotatedCoords(width, height, rotate) {
    const rotatePoint = (x, y, angle) => ({
        x: x * Math.cos(angle) - y * Math.sin(angle),
        y: x * Math.sin(angle) + y * Math.cos(angle),
    });
    const topLeft = rotatePoint(-width / 2, -height / 2, rotate);
    const topRight = rotatePoint(width / 2, -height / 2, rotate);
    const bottomLeft = rotatePoint(-width / 2, height / 2, rotate);
    const bottomRight = rotatePoint(width / 2, height / 2, rotate);
    const minX = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    const maxX = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    const minY = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    const maxY = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    return { minimumWidth: maxX - minX, minimumHeight: maxY - minY };
}

/**
 * 将图片按角度离屏旋转后导出 base64（供 ImageGroundLayer 使用）。
 * @param {string} imgSrc
 * @param {number} width
 * @param {number} height
 * @param {number} rotate 角度（deg）
 * @param {{ minimumWidth?: number, minimumHeight?: number }} [rotatedRect]
 * @returns {Promise<string>}
 */
export function getRotatedImg(imgSrc, width, height, rotate, rotatedRect = {}) {
    const rotationRadians = (rotate * Math.PI) / 180;
    const { minimumWidth, minimumHeight } = getRotatedCoords(width, height, rotationRadians);
    rotatedRect.minimumWidth = Math.ceil(minimumWidth);
    rotatedRect.minimumHeight = Math.ceil(minimumHeight);

    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = imgSrc;
        image.setAttribute('crossOrigin', 'anonymous');
        image.onload = () => {
            const cav = document.createElement('canvas');
            const ctx = cav.getContext('2d');
            const devicePixelRatio = window.devicePixelRatio || window.webkitDevicePixelRatio || window.mozDevicePixelRatio || 1;
            ctx.fillStyle = 'rgba(255, 255, 255, 0)';
            cav.width = minimumWidth * devicePixelRatio;
            cav.height = minimumHeight * devicePixelRatio;
            cav.style.width = `${minimumWidth}px`;
            cav.style.height = `${minimumHeight}px`;
            ctx.scale(devicePixelRatio, devicePixelRatio);
            ctx.translate(minimumWidth / 2, minimumHeight / 2);
            ctx.rotate(rotationRadians);
            ctx.drawImage(image, -width / 2, -height / 2, width, height);
            resolve(cav.toDataURL('image/png', 1));
        };
        image.onerror = err => reject(err);
    });
}

// ---------------------------- 商圈图层 zoom 显隐 ----------------------------

/**
 * 商圈 SURFACE 平铺层是否应在当前 zoom 显示（lite 无 AREA 层，仅依据 surfaceBase 区间）。
 * @param {number} zoom
 * @param {{ minZoom: number, maxZoom: number }|null|undefined} layerZoomRange
 */
export function isSurfaceLayerVisibleAtZoom(zoom, layerZoomRange) {
    if (!layerZoomRange) return false;
    return zoom >= layerZoomRange.minZoom && zoom <= layerZoomRange.maxZoom;
}

/** 当前 zoom 是否处于商圈层及以下（商圈 2D 显示模式条件） */
export function isAtOrBelowSurfaceMaxZoom(zoom, surfaceMaxZoom) {
    return Number.isFinite(surfaceMaxZoom) && zoom <= surfaceMaxZoom;
}

/**
 * 商圈层 2D 显示模式开关：enabled 为 true 时强制 2D，否则恢复 3D。
 * @param {Object} map 腾讯地图实例
 * @param {boolean} enabled 是否启用商圈 2D 显示（由 zoom 条件计算后传入）
 */
export function toggleSurfaceMapDisplayMode(map, enabled) {
    if (!map?.setViewMode) return;

    if (enabled) {
        map.setRotation?.(0);
        map.setPitch?.(0);
        map.setViewMode('2D');
        return;
    }

    map.setViewMode('3D');
}

/** 商圈 2D 模式下矢量底图仅保留 base（不展示道路 3d/2d 及 POI） */
export function applySurfaceDisplayVectorClamp(layers, enabled) {
    if (!enabled || !Array.isArray(layers)) return layers;

    return layers.map(layer => (layer.type === 'vector' ? { ...layer, features: ['base'] } : layer));
}

// ---------------------------- 颜色工具 ----------------------------

/** 去掉 rgba 字符串中的 alpha 通道展示形式 */
export function removeOpacityFromColor(color) {
    if (!color) return color;
    if (color.indexOf('rgba') > -1) {
        const [r, g, b, a] = color.substring(5, color.length - 1).split(',');
        return `rgb(${r},${g},${b},${a})`;
    }
    return color;
}

/** 判断 rgba 颜色是否完全透明（alpha === 0） */
export function isTransparentRgba(color) {
    const rgbaRegex = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(0|0\.\d+)\)$/;
    const match = color?.match?.(rgbaRegex);
    if (match) return parseFloat(match[4]) === 0;
    return false;
}

/** 将 hex / rgba 颜色转为指定 alpha 的 rgba 字符串 */
export function colorWithTransparency(color, alpha) {
    if (!color) return `rgba(0, 0, 0, ${alpha})`;
    let r;
    let g;
    let b;

    if (color.startsWith('#')) {
        const hex = color.slice(1);
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
    } else if (color.startsWith('rgba')) {
        const rgbaRegex = /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/;
        const matches = color.match(rgbaRegex);
        if (matches) {
            r = parseInt(matches[1], 10);
            g = parseInt(matches[2], 10);
            b = parseInt(matches[3], 10);
        } else {
            return color;
        }
    } else {
        return color;
    }

    const newAlpha = Math.min(Math.max(alpha, 0), 1);
    return `rgba(${r}, ${g}, ${b}, ${newAlpha})`;
}

// ---------------------------- 标签碰撞层级 ----------------------------

/** 面类型 → 碰撞基础层级（InfoWindow zIndex / MultiLabel rank） */
export const collisionLevelMap = {
    NEW_HOUSE: 8,
    SECOND_HAND_HOUSE: 7,
    SCHOOL: 6,
    PARK: 5,
    HOSPITAL: 4,
    BUSINESS: 3,
    GOVERNMENT: 2,
    COMPANY: 1,
    OTHER_TYPE: 1,
    RELOCATION_HOUSE: 1,
    DISADVANTAGE: 1,
    HOUSE: 1,
};

/**
 * 计算 MultiLabel / InfoWindow 碰撞 rank：类型权重 × 100 + 面积权重
 * @param {string} surfaceType
 * @param {number|string} polygonArea
 */
export function calculateRank(surfaceType, polygonArea) {
    const baseRank = collisionLevelMap[surfaceType] || 1;
    const areaRank = Math.max(0, Number(polygonArea));
    return baseRank * 100 + parseInt(areaRank, 10);
}

// ---------------------------- 日照模拟预览 ----------------------------

/** 模型版本：1 = V4，2 = V5 */
export const MODEL_TYPE = {
    V4: 1,
    V5: 2,
};

/** 平台管理员 uploadType，日照预览使用固定 tenantId */
const ADMIN_UPLOAD_TYPE = 'ADMIN';

/** 平台管理员 tenantId（V5 日照预览） */
const ADMIN_TENANT_ID = '8888888888888888888';

/**
 * 构建日照模拟全屏预览 URL。
 * V5 模型走 model-analysis/sun-light；V4 模型走 applet/solarView。
 * @param {{ id: string | number, modelType?: number, uploadType?: string }} item
 * @param {{ tenantId?: string }} [options]
 */
export function buildSunLightPreviewUrl(item, { tenantId } = {}) {
    const host = import.meta.env.VITE_APP_MAP_FRAME;

    if (item.modelType === MODEL_TYPE.V5) {
        const query = new URLSearchParams({
            modelId: item.id,
            tenantId: item.uploadType === ADMIN_UPLOAD_TYPE ? ADMIN_TENANT_ID : tenantId,
            showTypeSwitch: true,
        }).toString();
        return `${host}/model-analysis/sun-light?${query}`;
    }

    const query = new URLSearchParams({
        modelId: item.id,
        uploadType: item.uploadType,
    }).toString();
    return `${host}/applet/solarView?${query}`;
}
